import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const strong = password.length >= 12 && /[A-Z]/.test(password) && /[a-z]/.test(password) && /\d/.test(password);
    if (!strong) { setError("Password must be at least 12 characters and include an uppercase letter, a lowercase letter, and a number."); return; }
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated — please sign in");
      navigate("/login");
    } catch (err) { setError(apiError(err.response?.data?.detail) || "Reset failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center"><Building2 className="h-5 w-5 text-primary-foreground" /></div>
          <div className="leading-tight">
            <p className="font-display font-black tracking-tight text-xl">Bid Router</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground -mt-0.5">by Conduit Systems</p>
          </div>
        </div>
        <h1 className="font-display font-black text-3xl tracking-tight">Set a new password</h1>
        <p className="text-muted-foreground text-sm mt-1 mb-6">Choose a strong password for your account.</p>
        {!token ? (
          <p className="text-sm text-destructive" data-testid="reset-no-token">This reset link is missing its token. Please use the link from your email.</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">New password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="reset-password" className="mt-1.5" placeholder="••••••••" />
              <p className="text-xs text-muted-foreground mt-1">At least 12 characters, with an uppercase letter, a lowercase letter, and a number.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Confirm password</Label>
              <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm" className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-submit">{loading ? "Updating…" : "Update password"}</Button>
          </form>
        )}
        <Link to="/login" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mt-6" data-testid="back-to-login"><ArrowLeft className="h-4 w-4" />Back to sign in</Link>
      </div>
    </div>
  );
}
